import { z } from "zod";
import { leaveCopy } from "@/features/leave/copy";

const { refused } = leaveCopy;

const name = z
  .string({ error: refused.notAName })
  .trim()
  .min(1, { error: refused.notAName })
  .max(60, { error: refused.notAName });

const days = z
  .number({ error: refused.notDays })
  .int({ error: refused.notDays })
  .min(0, { error: refused.notDays })
  .max(366, { error: refused.notDays });

/** A whole type, as it is created and as an edit leaves it. */
export const leaveTypeInput = z
  .object({ name, paid: z.boolean(), daysAYear: days, carryOverDays: days })
  .refine(
    (type) => type.paid || (type.daysAYear === 0 && type.carryOverDays === 0),
    { error: refused.unpaidHasNoDays },
  );

/** An edit names only what it changes. */
export const leaveTypeChange = z.object({
  name: name.optional(),
  paid: z.boolean().optional(),
  daysAYear: days.optional(),
  carryOverDays: days.optional(),
});

/** The month a company's leave years start in, 1 for January. */
export const leaveYearInput = z.object({
  startMonth: z
    .number({ error: refused.notAMonth })
    .int({ error: refused.notAMonth })
    .min(1, { error: refused.notAMonth })
    .max(12, { error: refused.notAMonth }),
});

/** A year's allowance is at most 366 days, so a correction never needs more than that. */
export const adjustmentInput = z.object({
  leaveTypeId: z.string(),
  halfDays: z
    .number({ error: refused.notAnAdjustment })
    .int({ error: refused.notAnAdjustment })
    .min(-732, { error: refused.notAnAdjustment })
    .max(732, { error: refused.notAnAdjustment })
    .refine((value) => value !== 0, { error: refused.notAnAdjustment }),
  reason: z
    .string({ error: refused.notAReason })
    .trim()
    .min(1, { error: refused.notAReason })
    .max(500, { error: refused.notAReason }),
});

const day = z.iso.date({ error: refused.notADate });
const half = z.enum(["first", "second"], { error: refused.notADate });
const requestReason = z
  .string({ error: refused.notAReason })
  .trim()
  .min(1, { error: refused.notAReason })
  .max(500, { error: refused.notAReason });

const dates = {
  startsOn: day,
  endsOn: day,
  half: half.nullish().transform((value) => value ?? null),
};

type Dates = { startsOn: string; endsOn: string; half: LeaveHalf | null };

/**
 * A half day is one day. A range reaches into the next leave year at most, which the repository
 * judges by the company's start month; three calendar years apart is past that whatever the month.
 */
function checkDates(range: Dates, context: z.RefinementCtx) {
  if (range.startsOn > range.endsOn) {
    context.addIssue({ code: "custom", message: refused.datesOutOfOrder });
  } else if (range.half && range.startsOn !== range.endsOn) {
    context.addIssue({ code: "custom", message: refused.halfIsOneDay });
  } else if (
    Number(range.endsOn.slice(0, 4)) - Number(range.startsOn.slice(0, 4)) >
    2
  ) {
    context.addIssue({ code: "custom", message: refused.tooLong });
  }
}

/** The range a request covers, in one type. */
const leaveRange = z
  .object({ leaveTypeId: z.string(), ...dates })
  .superRefine(checkDates);

/** What the form counts while it is filled in: the type may not be picked yet. */
export const leaveCountSearch = z
  .object({ leaveTypeId: z.string().optional(), ...dates })
  .superRefine(checkDates);

/** Asking, or adding for somebody. Past the balance still sends. */
export const leaveAsk = leaveRange.and(
  z.object({
    reason: requestReason.nullish().transform((value) => value ?? null),
  }),
);

export const leaveYearSearch = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});

/** No range is the next 14 days. A range is at most a year, which a month or a request fits. */
export const leaveOffSearch = z
  .object({ from: day.optional(), to: day.optional() })
  .refine((range) => (range.from === undefined) === (range.to === undefined), {
    error: refused.notADate,
  })
  .refine((range) => !range.from || !range.to || range.from <= range.to, {
    error: refused.datesOutOfOrder,
  })
  .refine(
    (range) =>
      !range.from ||
      !range.to ||
      Date.parse(range.to) - Date.parse(range.from) <= 366 * 86_400_000,
    { error: refused.offTooLong },
  );

export const leaveCancel = z.object({ from: day.optional() });

export const leaveReasonChange = z.object({
  reason: requestReason.nullable(),
});

/** The note is optional; a blank one is stored as none. */
export const leaveDecline = z.object({
  reason: z
    .string({ error: refused.notADeclineNote })
    .trim()
    .max(500, { error: refused.notADeclineNote })
    .transform((note) => note || null)
    .nullish(),
});

export interface LeaveType {
  readonly id: string;
  readonly name: string;
  readonly paid: boolean;
  readonly daysAYear: number;
  readonly carryOverDays: number;
  /** Leave has been taken or asked of it: Paid is locked, and removing it archives it. */
  readonly used: boolean;
}

/** One paid type's balance for the leave year, in half days. */
export interface LeaveBalance {
  readonly leaveTypeId: string;
  readonly halfDays: number;
}

export type LeaveHalf = "first" | "second";
type LeaveRequestStatus =
  | "waiting"
  | "approved"
  | "declined"
  | "withdrawn"
  | "cancelled";

/** One row of a request. The rows of one ask share `groupId` and move together. */
export interface LeaveRequest {
  readonly id: string;
  readonly groupId: string;
  readonly personId: string;
  readonly leaveTypeId: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly half: LeaveHalf | null;
  readonly halfDays: number;
  readonly reason: string | null;
  readonly status: LeaveRequestStatus;
  readonly addedById: string | null;
  readonly decidedById: string | null;
  readonly declineReason: string | null;
  /** Who cancelled it, or the days cut from the end of an approved row. */
  readonly cancelledById: string | null;
  readonly askedAt: Date;
  readonly decidedAt: Date | null;
}

/** What stands of an ask: its approved rows once any are, so a cut-short ask counts what it kept. */
export function standing<Row extends { readonly status: string }>(
  rows: readonly Row[],
): readonly Row[] {
  const approved = rows.filter((row) => row.status === "approved");
  return approved.length > 0 ? approved : rows;
}

/** Somebody off in the range asked. An Employee never sees the type: sick leave is health data. */
export interface LeaveOff {
  readonly personId: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly half: LeaveHalf | null;
  /** The rest is a Manager's or an Admin's only. */
  readonly groupId?: string;
  readonly halfDays?: number;
  readonly leaveTypeId?: string;
  readonly addedById?: string | null;
  readonly decidedById?: string | null;
}

/** What the form shows while a range is picked, in half days. */
export interface LeaveCount {
  readonly halfDays: number;
  /** Balance less approved and waiting leave, per leave year touched; null for an unpaid type. */
  readonly left: { year: number; halfDays: number }[] | null;
  /** Past what is left, summed over the leave years; 0 for an unpaid type. */
  readonly overHalfDays: number;
}

export type LeaveTypeInput = z.infer<typeof leaveTypeInput>;
export type LeaveTypeChange = z.infer<typeof leaveTypeChange>;
export type AdjustmentInput = z.infer<typeof adjustmentInput>;
export type LeaveRange = z.infer<typeof leaveRange>;
export type LeaveCountSearch = z.input<typeof leaveCountSearch>;
/** What a caller sends; the schema fills in what it may leave out. */
export type LeaveAsk = z.input<typeof leaveAsk>;
export type LeaveCancel = z.infer<typeof leaveCancel>;
export type LeaveOffSearch = z.input<typeof leaveOffSearch>;
export type LeaveReasonChange = z.infer<typeof leaveReasonChange>;
export type LeaveDecline = z.infer<typeof leaveDecline>;
export type LeaveYearInput = z.infer<typeof leaveYearInput>;
